const FILLER_WORDS = new Set(['ah', 'er', 'hmm', 'like', 'uh', 'um']);
const LIGHT_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'be', 'for', 'from', 'in', 'is', 'it', 'of', 'on', 'or', 'that', 'the', 'then', 'to', 'was', 'we']);
const MOVEMENT_WORDS = /\b(approach|back|bend|block|carry|close|cross|enter|exit|face|gesture|kneel|leave|lift|look|move|open|pick up|point|raise|reach|sit|stand|step|turn|walk)\b/i;
const CONTRACTIONS = [
  [/\bwon['’]?t\b/gi, 'will not'], [/\bcan['’]?t\b/gi, 'can not'], [/\bshan['’]?t\b/gi, 'shall not'],
  [/\bdon['’]?t\b/gi, 'do not'], [/\bdoesn['’]?t\b/gi, 'does not'], [/\bdidn['’]?t\b/gi, 'did not'],
  [/\bisn['’]?t\b/gi, 'is not'], [/\baren['’]?t\b/gi, 'are not'], [/\bwasn['’]?t\b/gi, 'was not'],
  [/\bweren['’]?t\b/gi, 'were not'], [/\bshouldn['’]?t\b/gi, 'should not'], [/\bwouldn['’]?t\b/gi, 'would not'],
  [/\bcouldn['’]?t\b/gi, 'could not'], [/\bi['’]?m\b/gi, 'i am'], [/\bi['’]?ve\b/gi, 'i have'],
  [/\bwe['’]?re\b/gi, 'we are'], [/\byou['’]?re\b/gi, 'you are'], [/\bthey['’]?re\b/gi, 'they are'],
  [/\bit['’]?s\b/gi, 'it is'], [/\bthat['’]?s\b/gi, 'that is'], [/\blet['’]?s\b/gi, 'let us'],
];

// Recognition is deliberately kept closed briefly after generated speech ends.
// This prevents speaker/earpiece echo from satisfying the actor's own line.
// The cue is private earpiece audio, so the microphone only needs a short tail
// guard before listening. A longer gap caused prompt actors to say short lines
// before recognition had even started.
export const TTS_RECOGNITION_GUARD_MS = 120;
export const LIVE_DIRECTION_MAX_WORDS = 10;

export function normalizeSpokenLine(value) {
  let normalized = String(value ?? '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  for (const [pattern, replacement] of CONTRACTIONS) normalized = normalized.replace(pattern, replacement);
  return normalized.replace(/[^a-z0-9\s'-]/g, ' ').replace(/['’-]/g, ' ').replace(/\s+/g, ' ').trim();
}

function tokens(value, removeFillers = false) {
  const list = normalizeSpokenLine(value).split(' ').filter(Boolean);
  return removeFillers ? list.filter((token) => !FILLER_WORDS.has(token)) : list;
}

function tokenWeight(token) { return LIGHT_WORDS.has(token) ? 0.55 : 1.5; }

function weightedOverlap(expected, spoken) {
  const available = new Map();
  for (const token of spoken) available.set(token, (available.get(token) || 0) + 1);
  let matched = 0;
  let total = 0;
  let importantMatched = 0;
  let importantTotal = 0;
  for (const token of expected) {
    const weight = tokenWeight(token);
    total += weight;
    if (weight > 1) importantTotal += weight;
    if ((available.get(token) || 0) > 0) {
      matched += weight;
      if (weight > 1) importantMatched += weight;
      available.set(token, available.get(token) - 1);
    }
  }
  return {
    coverage: total ? matched / total : 0,
    importantCoverage: importantTotal ? importantMatched / importantTotal : (total ? matched / total : 0),
  };
}

function weightedPrecision(expected, spoken) {
  const available = new Map();
  for (const token of expected) available.set(token, (available.get(token) || 0) + 1);
  let matched = 0;
  let total = 0;
  for (const token of spoken) {
    const weight = tokenWeight(token);
    total += weight;
    if ((available.get(token) || 0) > 0) {
      matched += weight;
      available.set(token, available.get(token) - 1);
    }
  }
  return total ? matched / total : 0;
}

function sequenceCoverage(expected, spoken) {
  const rows = Array.from({ length: expected.length + 1 }, () => new Uint16Array(spoken.length + 1));
  for (let left = 1; left <= expected.length; left += 1) {
    for (let right = 1; right <= spoken.length; right += 1) {
      rows[left][right] = expected[left - 1] === spoken[right - 1]
        ? rows[left - 1][right - 1] + 1
        : Math.max(rows[left - 1][right], rows[left][right - 1]);
    }
  }
  return expected.length ? rows[expected.length][spoken.length] / expected.length : 0;
}

export function matchExpectedLine(expectedText, spokenText, options = {}) {
  const expected = tokens(expectedText);
  const spoken = tokens(spokenText, true);
  const isFinal = options.isFinal === true;
  const confidenceValue = Number(options.confidence);
  const confidence = Number.isFinite(confidenceValue) && confidenceValue > 0 ? Math.max(0, Math.min(1, confidenceValue)) : null;
  const elapsedMs = Math.max(0, Number(options.elapsedMs) || 0);
  if (!expected.length || !spoken.length) return { matched: false, score: 0, coverage: 0, importantCoverage: 0, reason: 'empty' };
  const { coverage, importantCoverage } = weightedOverlap(expected, spoken);
  const precision = weightedPrecision(expected, spoken);
  const sequence = sequenceCoverage(expected, spoken);
  const score = Math.max(0, Math.min(1, (coverage * 0.5) + (sequence * 0.3) + (precision * 0.2)));
  const exact = expected.length === spoken.length && expected.every((token, index) => token === spoken[index]);
  let matched = false;
  let reason = isFinal ? 'below-threshold' : 'partial';
  if (isFinal && elapsedMs >= 250) {
    if (expected.length === 1) {
      matched = exact && (confidence === null || confidence >= 0.85);
      reason = matched ? 'exact-short-line' : 'ambiguous-short-line';
    } else if (expected.length === 2) {
      matched = exact && (confidence === null || confidence >= 0.78);
      reason = matched ? 'exact-short-line' : 'ambiguous-short-line';
    } else {
      const confidenceSafe = confidence === null || confidence >= 0.25;
      const coverageFloor = expected.length <= 5 ? 0.72 : 0.68;
      const importantFloor = expected.length <= 5 ? 0.75 : 0.68;
      matched = confidenceSafe && coverage >= coverageFloor && importantCoverage >= importantFloor && sequence >= 0.62 && score >= 0.72;
      reason = matched ? 'expected-line-matched' : 'below-threshold';
    }
  }
  return {
    matched,
    score: Number(score.toFixed(4)),
    coverage: Number(coverage.toFixed(4)),
    importantCoverage: Number(importantCoverage.toFixed(4)),
    sequenceCoverage: Number(sequence.toFixed(4)),
    reason,
  };
}

export function matchExpectedLineResults(expectedText, recognitionResults, options = {}) {
  const results = Array.from(recognitionResults || []);
  let best = null;
  // Check the full results and the finalized prefix only. Scanning every
  // historic final-ended range repeats costly sequence matching on phones.
  let finalEnd = results.length;
  while (finalEnd > 0 && results[finalEnd - 1]?.isFinal !== true) finalEnd -= 1;
  const ends = finalEnd > 0 && finalEnd !== results.length ? [results.length, finalEnd] : [results.length];
  for (const end of ends) {
    for (let start = 0; start < end; start += 1) {
    const selected = results.slice(start, end);
    for (let alternativeIndex = 0; alternativeIndex < 3; alternativeIndex += 1) {
      const parts = [];
      const confidences = [];
      for (const result of selected) {
        const alternative = result?.[alternativeIndex] || result?.[0];
        const transcript = String(alternative?.transcript || '').trim();
        if (transcript) parts.push(transcript);
        const confidence = Number(alternative?.confidence);
        if (Number.isFinite(confidence) && confidence > 0) confidences.push(confidence);
      }
      if (!parts.length) continue;
      const isFinal = selected.every((result) => result?.isFinal === true);
      const confidence = confidences.length ? confidences.reduce((sum, value) => sum + value, 0) / confidences.length : undefined;
      const check = matchExpectedLine(expectedText, parts.join(' '), { isFinal, confidence, elapsedMs: options.elapsedMs });
      const candidate = { ...check, final: isFinal };
      if (!best || (candidate.matched && !best.matched) || (candidate.matched === best.matched && candidate.score > best.score)) best = candidate;
    }
    }
  }
  return best || { matched: false, score: 0, coverage: 0, importantCoverage: 0, sequenceCoverage: 0, reason: 'empty', final: false };
}

export function sceneSettingText(scene = {}) {
  const supplied = String(scene.setting || scene.location || scene.title || '').replace(/^\s*(INT\.?\/?EXT\.?|EXT\.?\/?INT\.?|INT\.?|EXT\.?)\s*/i, '').trim();
  return supplied ? `Setting: ${supplied.replace(/[.!?]+$/, '')}.` : '';
}

function finishInstruction(value) {
  const clean = String(value || '').trim().replace(/[,:;\-–—]+$/, '').replace(/\s+(a|an|and|but|my|our|the|their|then|to|with|your)$/i, '').trim();
  if (!clean) return '';
  return /[.!?]$/.test(clean) ? clean : `${clean}.`;
}

const ORDER_SENSITIVE_DIRECTION = /\b(before|after|until|unless|while|without|never|not|do not|does not|did not|cannot|can't|won't)\b/i;
const IMPERATIVE_START = /^(?:address|be|bend|block|carry|close|cross|enter|exit|face|gesture|hold|keep|kneel|leave|lift|look|lower|move|open|pick|point|raise|reach|react|sit|stand|start|step|stop|study|take|turn|wait|walk)\b/i;

function completeDirectionBeats(clean, limit) {
  // A common blocking direction can be shortened without losing its temporal
  // contract: do the approach, wait for the named condition, then open.
  const waitBeforeOpen = clean.match(/^(.+?)\s+and\s+wait\s+until\s+(.+?)\s+before\s+opening\s+(.+?)[.!?]*$/i);
  if (waitBeforeOpen) return [
    finishInstruction(waitBeforeOpen[1]),
    finishInstruction(`Wait until ${waitBeforeOpen[2]}`),
    finishInstruction(`Then open ${waitBeforeOpen[3]}`),
  ].join(' ');
  const thoughts = clean.match(/[^.!?]+[.!?]?/g) || [clean];
  const beats = [];
  for (const thought of thoughts) {
    const body = thought.trim().replace(/[.!?]+$/, '').trim();
    if (!body) continue;
    if (body.split(/\s+/).length <= limit || ORDER_SENSITIVE_DIRECTION.test(body)) {
      beats.push(finishInstruction(body));
      continue;
    }
    const parts = body.split(/\s*[,;]\s*|\s+then\s+/i)
      .map(part => part.replace(/^(?:and|then)\s+/i, '').trim()).filter(Boolean);
    if (parts.length > 1 && parts.every(part => IMPERATIVE_START.test(part))) {
      beats.push(...parts.map(part => finishInstruction(part.replace(/^([a-z])/, letter => letter.toUpperCase()))));
    } else beats.push(finishInstruction(body));
  }
  return beats.join(' ');
}

// Shorten presentation only when every authored action survives. Timing,
// negation, targets and before/after/until relationships are never truncated.
export function conciseStageDirection(value, maxWords = LIVE_DIRECTION_MAX_WORDS) {
  const limit = Math.max(1, Math.floor(Number(maxWords) || LIVE_DIRECTION_MAX_WORDS));
  // Strip only an explicit leading performance label, never the actor's dialogue.
  const clean = String(value || '').replace(/[{}\[\]]/g, '').replace(/\s+/g, ' ').trim()
    .replace(/^(?:(?:stage\s+directions?|directions?|acting\s+cues?)\s*[:.]\s*)+/i, '');
  if (!clean) return '';
  if (clean.split(/\s+/).length <= limit) return finishInstruction(clean);
  return completeDirectionBeats(clean, limit);
}

export function conciseSituationUpdate(value, maxWords = 7) {
  const clean = String(value || '')
    .replace(/^(.{1,48}?)\s+has(?: just)?\s+(admitted|concluded|discovered|explained|learned|reported|revealed|said|shown|told us)\s+that\s+/i, '')
    .replace(/^(.{1,48}?)\s+just\s+(admitted|concluded|discovered|explained|learned|reported|revealed|said|shown|told us)\s+that\s+/i, '')
    .trim();
  return conciseStageDirection(clean || value, maxWords);
}

export const SIMULTANEOUS_LINE_FALLBACK_GRACE_MS = 1250;
export const SPEAK_ALONG_RECOGNITION_GRACE_MS = 2500;

export function estimateSpokenLineDurationMs(value) {
  const wordCount = tokens(value).length;
  const punctuationPauses = (String(value || '').match(/[,.!?;:]/g) || []).length;
  return Math.max(4_000, Math.min(15 * 60_000, 2_200 + (wordCount * 620) + (punctuationPauses * 180)));
}

export function estimateRecognitionSafetyDurationMs(value) {
  return Math.min(15 * 60_000, Math.max(15_000, estimateSpokenLineDurationMs(value) + 8_000));
}

export function estimateActiveSpeechSafetyDurationMs(value) {
  return Math.min(15 * 60_000, estimateSpokenLineDurationMs(value) + 3_000);
}

export function cueHasMovement(cue = {}) {
  const direction = String(cue.directionText || (cue.type === 'direction' || cue.type === 'action' ? cue.text : ''));
  return Boolean(direction && (MOVEMENT_WORDS.test(direction) || cue.completionMode === 'movement' || Number(cue.estimatedDurationMs) > 0));
}

export function cueDialogueText(cue = {}) {
  return String(cue.dialogueText || (cue.type === 'dialogue' || cue.cueVoice ? cue.text : '')).trim();
}

export function completionModeForCue(cue = {}) {
  if (cueDialogueText(cue)) return 'speech';
  if (Number(cue.estimatedDurationMs) > 0 || cue.completionMode === 'timer') return 'timer';
  if (cue.visibility === 'character') return 'actor-confirm';
  return 'director';
}

export function estimateMovementDurationMs(text) {
  const wordCount = tokens(text).length;
  return Math.max(2000, Math.min(10_000, Math.ceil(wordCount / 4) * 1000));
}
